import type { MatasPlusOfferNudgeModel } from 'autogen/swagger/all';
import {
    GetProductInformationQueryKey,
    useGetIsMatasPlusOfferNudgeActiveQuery,
    useTogglePlusSignupMutation
} from 'autogen/swagger/all/hooks';
import {
    AddToBasketError,
    AddToBasketSuccess,
    OpenDetailsButtonText
} from 'autogen/translation-keys/trans-website-matasplus-offernudge';
import Card from 'components/generic/card';
import InlineButton from 'components/generic/InlineButton';
import ControlledToggleButton from 'components/generic/Toggle/components/ControlledToggleButton';
import { Typography } from 'components/generic/typography';
import Icon from 'components/Icon';
import List from 'components/List';
import format from 'helpers/translations';
import { GA4Tracking } from 'modules/tracking/GA4';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import ThemeProvider from 'theming/ThemeProvider';
import toast from 'components/Toast';
import usePlusOfferNudgeQuickView from './components/PlusOfferNudgeQuickView/hooks/usePlusOfferNudgeQuickView';
import { updateBasket } from 'components/basket/shared/basket';
import usePlusSignUpQuickView from './components/PlusSignUpQuickView/hooks/usePlusSignUpQuickView';
import queryClient from 'setup/react-query/queryClient';
import { useGetProductListPartial } from 'components/shared/productList/shared/hooks/useGetProductListPartialQuery';

export type PlusOfferNudgeProps = {
    model: MatasPlusOfferNudgeModel;
};

const StyledCard = styled(Card)`
    border: 1px solid ${({ theme }) => theme.ColorBorderPrimary};
    background: ${({ theme }) => theme.ColorBackgroundPrimary};
`;

const StyledCardContent = styled(Card.Content)<{ isToggleActive: boolean }>`
    display: grid;
    grid-template-columns: 1fr max-content;
    gap: ${({ theme }) => theme.Spacing8};

    ${({ isToggleActive }) =>
        isToggleActive
            ? css`
                grid-template-areas:
                    'heading details'
                    'body toggle'
                    'benefits benefits'
                ;`
            : css`
                grid-template-areas:
                    'heading details'
                    'body toggle';
                `}
`;

const StyledHeading = styled(Typography.Body)`
    grid-area: heading;
`;

const StyledBody = styled(Typography.Body)`
    grid-area: body;
`;

const StyledInlineButton = styled(InlineButton)`
    grid-area: details;
    justify-self: end;
    align-self: center;
`;

const StyledToggle = styled(ControlledToggleButton)`
    grid-area: toggle;
    justify-self: end;
    align-self: self-end;
`;

const StyledList = styled(List)`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing6};
`;

const BenefitListItem = styled.li`
    grid-area: benefits;
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.Spacing6};
    padding: 0;
`;

function PlusOfferNudge(props: PlusOfferNudgeProps) {
    const { model, ...cardProps } = props;
    const { refetch: refetchProductList } = useGetProductListPartial();

    const { data: isActiveState } = useGetIsMatasPlusOfferNudgeActiveQuery({
        initialData: model.isActive,
        onSuccess(data) {
            setIsToggleActive((prevState) => {
                if (prevState !== data) {
                    queryClient.invalidateQueries({ queryKey: [GetProductInformationQueryKey] });
                    refetchProductList();
                }
                return data;
            });
        }
    });

    const { heading, bodyText, isActive, memberBenefits, quickViewModel, promptLoginModel } = model;
    const [isToggleActive, setIsToggleActive] = useState<boolean>(isActiveState || isActive);
    const { mutateAsync: togglePlus } = useTogglePlusSignupMutation();
    const [, togglePlusSignUp] = usePlusSignUpQuickView();
    const [, toggle] = usePlusOfferNudgeQuickView();
    const { t } = useTranslation();

    async function handleToggleOnChange() {
        try {
            setIsToggleActive(!isToggleActive);
            const { success } = await togglePlus({ params: { enable: !isToggleActive } });
            if (!success) throw new Error('failed to add PlusSignup');

            updateBasket();

            queryClient.invalidateQueries({ queryKey: [GetProductInformationQueryKey] });
            refetchProductList();

            GA4Tracking({
                eventName: 'toggle_subscription',
                category: 'ecommerce',
                context: 'matas_plus',
                eventParams: [{ param_name: 'toggle', param_value: !isToggleActive }]
            });

            if (!isToggleActive && promptLoginModel) {
                togglePlusSignUp({
                    isOpen: true,
                    model: promptLoginModel
                });
            }

            if (!isToggleActive && !promptLoginModel) {
                toast.success(format(t(AddToBasketSuccess)));
            }
        } catch (error) {
            setIsToggleActive(false);
            toast.error(t(AddToBasketError));
            console.error(error);
        }
    }

    function handleOpenQuickView() {
        toggle({
            isOpen: true,
            model: quickViewModel
        });
    }

    return (
        <ThemeProvider theme="club2">
            <StyledCard {...cardProps}>
                <StyledCardContent isToggleActive={isToggleActive}>
                    <StyledHeading size="L" strong>
                        {heading}
                    </StyledHeading>
                    <StyledBody size="S">{format(bodyText)}</StyledBody>
                    <StyledInlineButton size="S" onClick={handleOpenQuickView}>
                        {t(OpenDetailsButtonText)}
                    </StyledInlineButton>
                    <StyledToggle active={isToggleActive} onChange={handleToggleOnChange} />
                    {isToggleActive && (
                        <StyledList>
                            {memberBenefits.map((benefit, i) => (
                                <BenefitListItem key={i}>
                                    <Icon type="Checkmark" size={16} />
                                    <Typography.Body size="S">{benefit}</Typography.Body>
                                </BenefitListItem>
                            ))}
                        </StyledList>
                    )}
                </StyledCardContent>
            </StyledCard>
        </ThemeProvider>
    );
}

export default PlusOfferNudge;
